import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { com } from '@eidu/form'
import { EntityTypesContext } from './EntityTypes'
import useEntitiesOfType from '../useEntitiesOfType'
import { ADMIN_SCHEMA_NAME, formatTypeName } from '../../util/entityType/typeNameSchema'
import { fieldValueToString } from '../../domain/entity/DraftFieldValue'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import FieldType = com.eidu.sharedlib.entity.field.FieldType

type SettingsContextValue = {
  getSettingValue: (key: string) => string | undefined
}

const SettingsContext = createContext<SettingsContextValue>({ getSettingValue: () => undefined })

const SETTINGS_TYPE_NAME = formatTypeName(ADMIN_SCHEMA_NAME, 'Settings')

const normalize = (value: string) => value.trim().toLowerCase()

const findTypeByName = (types: EntityType[], targetName: string) =>
  types.find((type) => normalize(type.name) === normalize(targetName))

const findFieldByName = (type: EntityType, name: string) =>
  type.fields.asJsReadonlyArrayView().find((field) => normalize(field.name) === normalize(name))

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const entityTypeRepository = useContext(EntityTypesContext)

  useEffect(() => {
    entityTypeRepository.getAll().then()
  }, [entityTypeRepository])

  const entityTypes = entityTypeRepository.useSortedEntityTypes() ?? []
  const entityTypesMap = entityTypeRepository.useAll()

  const settingsType = useMemo(() => findTypeByName(entityTypes, SETTINGS_TYPE_NAME), [entityTypes])

  const settingsEntitiesByType = useEntitiesOfType(
    {
      typeIds: settingsType && entityTypesMap ? [settingsType.id] : [],
      allTypes: entityTypesMap ?? new Map(),
      pageSize: 200,
    },
    [settingsType?.id.toString() ?? '', entityTypesMap?.size ?? 0]
  )

  const settingsEntities = settingsType ? settingsEntitiesByType?.get(settingsType.id) : undefined

  const settingsMap = useMemo(() => {
    if (!settingsType || !settingsEntities) return new Map<string, string>()
    const keyField = findFieldByName(settingsType, 'Key')
    const valueField = findFieldByName(settingsType, 'Value')
    if (!keyField || !valueField) return new Map<string, string>()
    if (keyField.type !== FieldType.Text || valueField.type !== FieldType.Text) return new Map<string, string>()

    const map = new Map<string, string>()
    settingsEntities.forEach((entity) => {
      if (!entity) return
      const values = entity.entity.valuesByFieldId.asJsReadonlyMapView()
      const key = fieldValueToString(values.get(keyField.id) ?? null).trim()
      if (!key) return
      const value = fieldValueToString(values.get(valueField.id) ?? null).trim()
      map.set(normalize(key), value)
    })
    return map
  }, [settingsEntities, settingsType])

  const getSettingValue = useCallback((key: string) => settingsMap.get(normalize(key)), [settingsMap])

  const contextValue = useMemo(() => ({ getSettingValue }), [getSettingValue])

  return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>
}

export const useSettings = () => useContext(SettingsContext)
